/**
 * Maison Porte-Manteau — fiche produit (01/11/2026).
 * Chargé une fois par blocks/_product-media-gallery.liquid (module, différé).
 *
 * 1. Galerie : au changement de couleur, la 1re photo de la nouvelle série s'affiche tout de suite
 *    (modèle <template data-mpm-vedette> rendu par snippets/mpm-galerie-produit.liquid), le temps
 *    qu'Horizon remplace <media-gallery> par la galerie filtrée de la page renvoyée par le serveur.
 * 2. Loupe et visionneuse plein écran (<dialog> modal) : photos de la couleur affichée, taille réelle
 *    au plus, flèches, compteur, clavier, balayage, boucle, fondu, préchargement des voisines.
 * 3. Récits (sections/mpm-recits.liquid) : images de la couleur choisie, apparition douce au défilement.
 * 4. « Découvrez également » (sections/mpm-decouvrir.liquid) : recommandations Shopify, piste horizontale
 *    (barre de progression, boutons ‹ ›, glisser à la souris).
 * 5. Préchargements (tâche de fond après le chargement, survol d'une couleur, survol ou focus de la loupe) :
 *    1re photo de chaque autre couleur (mêmes srcset et sizes que la grande image de la galerie) et photos
 *    1024 de la couleur affichée pour la visionneuse.
 */
import { lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const EVENEMENT_SELECTION = 'shopify:product:select';
const SVG = (chemin) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true" focusable="false"><path d="${chemin}" vector-effect="non-scaling-stroke"/></svg>`;

document.documentElement.classList.add('mpm-produit-js');

/** Taille d'affichage des photos de la visionneuse (1024 px au plus). */
const SIZES_VISIONNEUSE = '(min-width: 1072px) 1024px, calc(100vw - 32px)';
const ECONOMIE_DONNEES = Boolean(/** @type {any} */ (navigator).connection?.saveData);

/**
 * Lance une tâche quand le navigateur est libre (repli : minuteur).
 * @param {() => void} tache
 * @param {number} [delai]
 */
function quandLibre(tache, delai = 2000) {
  if ('requestIdleCallback' in window) window.requestIdleCallback(() => tache(), { timeout: delai });
  else window.setTimeout(tache, Math.min(delai, 1200));
}

/** @type {Map<string, {image: HTMLImageElement, pret: boolean, promesse: Promise<boolean>}>} */
const prechargements = new Map();

/**
 * Précharge et décode une image hors du document. Avec les mêmes srcset et sizes qu'une image affichée,
 * le navigateur choisit le même fichier : il est ensuite repris du cache, sans attente.
 * @param {string} src
 * @param {string} srcset
 * @param {string} sizes
 */
function precharger(src, srcset, sizes) {
  const cle = `${src}|${srcset}|${sizes}`;
  const existant = prechargements.get(cle);
  if (existant) return existant;

  const image = new Image();
  image.decoding = 'async';
  if (sizes) image.sizes = sizes;
  if (srcset) image.srcset = srcset;
  image.src = src;

  const entree = { image, pret: false, promesse: Promise.resolve(false) };
  entree.promesse = new Promise((resolve) => {
    const fini = () => {
      if (!(image.complete && image.naturalWidth > 0)) return resolve(false);
      image.decode().then(
        () => resolve(true),
        () => resolve(true)
      );
    };
    if (image.complete) fini();
    else {
      image.addEventListener('load', fini, { once: true });
      image.addEventListener('error', () => resolve(false), { once: true });
    }
  }).then((ok) => {
    entree.pret = ok;
    return ok;
  });
  prechargements.set(cle, entree);
  return entree;
}

/**
 * Galerie de la fiche (hors ajout rapide et hors dialogue).
 * @returns {HTMLElement | null}
 */
function galeriePrincipale() {
  for (const galerie of document.querySelectorAll('media-gallery.mpm-galerie')) {
    if (galerie instanceof HTMLElement && !galerie.closest('dialog, .quick-add-modal__content')) return galerie;
  }
  return null;
}

/**
 * Précharge la 1re photo d'une couleur avec exactement le srcset et le sizes de la grande image de la galerie.
 * @param {Element} galerie
 * @param {HTMLTemplateElement} modele - <template data-mpm-vedette>
 */
function prechargerModele(galerie, modele) {
  const image = modele.content.querySelector('img');
  if (!image) return;
  const reference = galerie.querySelector('slideshow-slide .product-media__image');
  const sizes = reference?.getAttribute('sizes') || image.getAttribute('sizes') || '';
  precharger(image.getAttribute('src') || '', image.getAttribute('srcset') || '', sizes);
}

/** @param {Element} galerie */
function prechargerAutresCouleurs(galerie) {
  for (const modele of galerie.querySelectorAll('template[data-mpm-vedette]')) {
    if (modele instanceof HTMLTemplateElement) prechargerModele(galerie, modele);
  }
}

/** @param {Element} galerie - précharge les photos 1024 de la couleur affichée (visionneuse) */
function prechargerPhotosHD(galerie) {
  for (const photo of lirePhotos(galerie)) precharger(photo.src, photo.srcset || '', SIZES_VISIONNEUSE);
}

/**
 * Photo déjà chargée par la galerie pour ce média (affichée tout de suite dans la visionneuse).
 * @param {Element} galerie
 * @param {string | number} mediaId
 * @returns {string | null}
 */
function imageGalerie(galerie, mediaId) {
  for (const image of galerie.querySelectorAll(`.product-media[data-media-id="${String(mediaId)}"] img`)) {
    if (image instanceof HTMLImageElement && image.complete && image.naturalWidth > 0 && image.currentSrc) {
      return image.currentSrc;
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Outils                                                             */
/* ------------------------------------------------------------------ */

/**
 * Photos de la visionneuse, telles que rendues dans la galerie (groupe de la couleur affichée).
 * @param {Element} galerie
 * @returns {{id: number, src: string, srcset: string, w: number, h: number, l: number, alt: string}[]}
 */
function lirePhotos(galerie) {
  const script = galerie.querySelector('script[data-mpm-photos]');
  if (!script) return [];
  try {
    const donnees = JSON.parse(script.textContent || '[]');
    return Array.isArray(donnees) ? donnees.filter((photo) => photo && photo.src && photo.w && photo.h) : [];
  } catch {
    return [];
  }
}

/**
 * @param {Element} galerie
 * @returns {any} Le diaporama d'Horizon (slideshow-component), s'il existe.
 */
function diaporama(galerie) {
  return galerie.querySelector('slideshow-component');
}

/**
 * Identifiant du média affiché par la galerie (ou de la photo de la loupe cliquée, en mode grille).
 * @param {Element} galerie
 * @param {HTMLElement} bouton
 * @returns {string | null}
 */
function mediaAffiche(galerie, bouton) {
  if (bouton.dataset.mpmMedia) return bouton.dataset.mpmMedia;

  const slideshow = diaporama(galerie);
  let diapositive = null;
  if (slideshow && Array.isArray(slideshow.slides) && Number.isInteger(slideshow.current)) {
    diapositive = slideshow.slides[slideshow.current] ?? null;
  }
  if (!diapositive) diapositive = galerie.querySelector('slideshow-slide[aria-hidden="false"]');

  const media = diapositive?.querySelector('.product-media[data-media-id]');
  return media instanceof HTMLElement ? media.dataset.mediaId ?? null : null;
}

/**
 * Amène le diaporama de la galerie sur une photo, sans animation.
 * @param {Element} galerie
 * @param {string | number} mediaId
 */
function selectionnerDansGalerie(galerie, mediaId) {
  const slideshow = diaporama(galerie);
  if (!slideshow || typeof slideshow.select !== 'function') return;

  const diapositives = Array.isArray(slideshow.slides)
    ? slideshow.slides
    : Array.from(slideshow.querySelectorAll('slideshow-slide'));
  const index = diapositives.findIndex((diapositive) =>
    diapositive.querySelector(`.product-media[data-media-id="${String(mediaId)}"]`)
  );
  if (index < 0 || index === slideshow.current) return;

  try {
    const resultat = slideshow.select(index, undefined, { animate: false });
    if (resultat && typeof resultat.catch === 'function') resultat.catch(() => {});
  } catch {
    /* le diaporama n'est pas prêt : rien à faire */
  }
}

/* ------------------------------------------------------------------ */
/* 2. Visionneuse plein écran                                         */
/* ------------------------------------------------------------------ */

class Visionneuse {
  /** @type {HTMLDialogElement} */
  dialog;
  /** @type {HTMLElement} */
  scene;
  /** @type {HTMLElement} */
  navigation;
  /** @type {HTMLElement} */
  courant;
  /** @type {HTMLElement} */
  total;
  /** @type {ReturnType<typeof lirePhotos>} */
  photos = [];
  /** @type {HTMLImageElement[]} */
  images = [];
  index = -1;
  /** @type {HTMLElement | null} */
  declencheur = null;
  /** @type {Element | null} */
  galerie = null;
  defilement = 0;
  fermeture = false;
  minuterieVerrou = 0;
  dernierPointeur = 'mouse';
  /** @type {{x: number, y: number} | null} */
  depart = null;
  /** @type {Set<number>} */
  pointeurs = new Set();
  session = 0;

  constructor() {
    const dialog = document.createElement('dialog');
    dialog.className = 'mpm-visionneuse';
    dialog.setAttribute('aria-label', 'Photos du produit en grand');
    dialog.innerHTML = `
      <div class="mpm-visionneuse__scene"></div>
      <button type="button" class="mpm-visionneuse__fermer" aria-label="Fermer">${SVG('M6.5 6.5l11 11M17.5 6.5l-11 11')}</button>
      <div class="mpm-visionneuse__navigation">
        <button type="button" class="mpm-visionneuse__bouton" data-pas="-1" aria-label="Photo précédente">${SVG('M14.5 6.5 9 12l5.5 5.5')}</button>
        <p class="mpm-visionneuse__compteur" aria-live="polite" aria-atomic="true"><span class="visually-hidden">Photo </span><span data-mpm-courant>1</span><span aria-hidden="true"> / </span><span class="visually-hidden"> sur </span><span data-mpm-total>1</span></p>
        <button type="button" class="mpm-visionneuse__bouton" data-pas="1" aria-label="Photo suivante">${SVG('M9.5 6.5 15 12l-5.5 5.5')}</button>
      </div>`;

    this.dialog = dialog;
    this.scene = /** @type {HTMLElement} */ (dialog.querySelector('.mpm-visionneuse__scene'));
    this.navigation = /** @type {HTMLElement} */ (dialog.querySelector('.mpm-visionneuse__navigation'));
    this.courant = /** @type {HTMLElement} */ (dialog.querySelector('[data-mpm-courant]'));
    this.total = /** @type {HTMLElement} */ (dialog.querySelector('[data-mpm-total]'));

    dialog.addEventListener(
      'pointerdown',
      (event) => {
        this.dernierPointeur = event.pointerType || 'mouse';
      },
      true
    );

    dialog.querySelector('.mpm-visionneuse__fermer')?.addEventListener('click', () => this.fermer());

    for (const bouton of dialog.querySelectorAll('[data-pas]')) {
      if (!(bouton instanceof HTMLElement)) continue;
      bouton.addEventListener('click', () => this.aller(this.index + (Number(bouton.dataset.pas) || 1)));
    }

    // Un clic sur la photo referme (souris, pavé tactile) ; un toucher ne ferme pas.
    this.scene.addEventListener('click', (event) => {
      const cible = event.target instanceof Element ? event.target.closest('.mpm-visionneuse__photo.is-active') : null;
      if (!cible) return;
      if (this.dernierPointeur === 'touch' || this.dernierPointeur === 'pen') return;
      this.fermer();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.aller(this.index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.aller(this.index + 1);
      }
    });

    // Échap : fermeture avec fondu
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.fermer();
    });

    dialog.addEventListener('close', () => this.#apresFermeture());

    // Balayage sur écran tactile (un doigt, page non zoomée)
    this.scene.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch') return;
      this.pointeurs.add(event.pointerId);
      this.depart = this.pointeurs.size === 1 ? { x: event.clientX, y: event.clientY } : null;
    });
    this.scene.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch') return;
      this.pointeurs.delete(event.pointerId);
      const depart = this.depart;
      this.depart = null;
      if (!depart || (window.visualViewport && window.visualViewport.scale > 1.05)) return;
      const dx = event.clientX - depart.x;
      const dy = event.clientY - depart.y;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) this.aller(this.index + (dx < 0 ? 1 : -1));
    });
    this.scene.addEventListener('pointercancel', (event) => {
      this.pointeurs.delete(event.pointerId);
      this.depart = null;
    });

    document.body.append(dialog);
  }

  /**
   * @param {Element} galerie
   * @param {HTMLElement} declencheur
   * @param {string | null} mediaId
   */
  ouvrir(galerie, declencheur, mediaId) {
    if (this.dialog.open) return;
    const photos = lirePhotos(galerie);
    if (!photos.length) return;

    this.session += 1;
    this.galerie = galerie;
    this.declencheur = declencheur;
    this.photos = photos;
    this.images = photos.map((photo) => this.#creerImage(photo));
    this.scene.replaceChildren(...this.images);
    this.navigation.hidden = photos.length < 2;
    this.total.textContent = String(photos.length);

    let depart = photos.findIndex((photo) => String(photo.id) === String(mediaId));
    if (depart < 0) depart = 0;

    this.dialog.classList.add('is-instantane');
    this.dialog.classList.remove('is-fermeture');
    this.fermeture = false;
    this.index = -1;
    this.aller(depart);

    this.defilement = getScrollTop();
    this.dialog.showModal();

    // Défilement de la page bloqué à la fin du fondu d'apparition : si la barre de défilement disparaît,
    // la page se recale derrière un fond déjà opaque, sans saut visible pendant le fondu.
    window.clearTimeout(this.minuterieVerrou);
    this.minuterieVerrou = window.setTimeout(() => {
      if (this.dialog.open && !this.fermeture) lockScroll(this.dialog);
    }, 210);

    requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.remove('is-instantane')));
  }

  /**
   * Affiche la photo i (en boucle) et précharge ses voisines.
   * @param {number} i
   */
  aller(i) {
    const n = this.photos.length;
    if (!n) return;
    const cible = ((i % n) + n) % n;
    if (cible === this.index) return;
    this.index = cible;

    for (const k of [cible, cible + 1, cible - 1]) this.#charger(((k % n) + n) % n);

    this.images.forEach((image, k) => {
      const actif = k === cible;
      image.classList.toggle('is-active', actif);
      image.setAttribute('aria-hidden', actif ? 'false' : 'true');
    });
    this.courant.textContent = String(cible + 1);
  }

  /** Fermeture en fondu de 200 ms, y compris en « réduire les animations » (fondu sans déplacement). */
  fermer() {
    if (!this.dialog.open || this.fermeture) return;
    this.fermeture = true;

    // Fond encore opaque : la page retrouve d'abord sa barre de défilement, sa position et la photo
    // regardée en dernier ; le fondu de sortie la révèle déjà en place.
    this.#rendreLaPage();
    this.dialog.classList.add('is-fermeture');
    window.setTimeout(() => {
      if (this.dialog.open) this.dialog.close();
    }, 210);
  }

  #rendreLaPage() {
    window.clearTimeout(this.minuterieVerrou);
    unlockScroll(this.dialog);
    scrollTo({ top: this.defilement, behavior: 'instant' });

    // La galerie reprend la photo regardée en dernier
    const photo = this.photos[this.index];
    if (photo && this.galerie?.isConnected) selectionnerDansGalerie(this.galerie, photo.id);
  }

  #apresFermeture() {
    // Fermeture sans passer par fermer() (cas imprévu) : la page est rendue ici
    if (!this.fermeture) this.#rendreLaPage();
    this.dialog.classList.remove('is-fermeture');
    this.fermeture = false;

    // Le focus revient au bouton loupe
    let retour = this.declencheur;
    if (!retour?.isConnected) {
      retour = document.querySelector('.product-information media-gallery .mpm-loupe');
    }
    if (retour instanceof HTMLElement) retour.focus({ preventScroll: true });

    this.scene.replaceChildren();
    this.images = [];
    this.photos = [];
    this.index = -1;
    this.galerie = null;
    this.declencheur = null;
    this.pointeurs.clear();
    this.depart = null;
  }

  /**
   * @param {ReturnType<typeof lirePhotos>[number]} photo
   * @returns {HTMLImageElement}
   */
  #creerImage(photo) {
    const image = document.createElement('img');
    const largeur = Math.min(Number(photo.l) || Number(photo.w), Number(photo.w));
    image.className = 'mpm-visionneuse__photo';
    image.alt = photo.alt || '';
    image.width = largeur;
    image.height = Math.round((largeur * Number(photo.h)) / Number(photo.w));
    image.decoding = 'async';
    image.draggable = false;
    image.setAttribute('aria-hidden', 'true');
    image.style.setProperty('--mpm-l', String(largeur));
    image.style.setProperty('--mpm-r', String(Number(photo.w) / Number(photo.h)));
    return image;
  }

  /**
   * Affiche la photo k : la version 1024 si elle est déjà prête ; sinon, tout de suite l'image déjà chargée
   * par la galerie (même cadre, même taille d'affichage), remplacée par la 1024 dès qu'elle est décodée.
   * @param {number} k
   */
  #charger(k) {
    const image = this.images[k];
    const photo = this.photos[k];
    if (!image || !photo || image.dataset.etat) return;

    const hd = precharger(photo.src, photo.srcset || '', SIZES_VISIONNEUSE);
    if (hd.pret) {
      this.#poserHD(image, photo);
      return;
    }

    const provisoire = this.galerie ? imageGalerie(this.galerie, photo.id) : null;
    if (provisoire) {
      image.src = provisoire;
      image.dataset.etat = 'provisoire';
    } else {
      image.dataset.etat = 'attente';
    }

    const session = this.session;
    hd.promesse.then((ok) => {
      if (session !== this.session || !image.isConnected) return;
      if (ok || image.dataset.etat === 'attente') this.#poserHD(image, photo);
    });
  }

  /**
   * Même srcset et même sizes que le préchargement : le fichier vient du cache, sans clignotement.
   * @param {HTMLImageElement} image
   * @param {ReturnType<typeof lirePhotos>[number]} photo
   */
  #poserHD(image, photo) {
    image.sizes = SIZES_VISIONNEUSE;
    if (photo.srcset) image.srcset = photo.srcset;
    image.src = photo.src;
    image.dataset.etat = 'hd';
  }
}

/** @type {Visionneuse | null} */
let visionneuseUnique = null;

function visionneuse() {
  if (!visionneuseUnique) visionneuseUnique = new Visionneuse();
  return visionneuseUnique;
}

/** @param {Event} event */
function surLoupe(event) {
  const bouton = event.target instanceof Element ? event.target.closest('.mpm-loupe') : null;
  if (!bouton || bouton.closest('.quick-add-modal__content, quick-add-dialog')) return;
  const galerie = bouton.closest('media-gallery');
  if (!(galerie instanceof HTMLElement) || galerie.dataset.mpmHd === 'oui') return;
  galerie.dataset.mpmHd = 'oui';
  prechargerPhotosHD(galerie);
}
document.addEventListener('pointerover', surLoupe, { passive: true });
document.addEventListener('focusin', surLoupe);

// Survol d'une couleur : préchargement de sa 1re photo (en plus du préchargement de fond)
document.addEventListener(
  'pointerover',
  (event) => {
    const cible = event.target instanceof Element ? event.target : null;
    if (!cible || !cible.closest('variant-picker') || cible.closest('product-card, dialog')) return;
    const etiquette = cible.closest('label');
    let champ = cible.closest('input[data-variant-id]') || etiquette?.querySelector('input[data-variant-id]');
    if (!champ && etiquette instanceof HTMLLabelElement && etiquette.htmlFor) champ = document.getElementById(etiquette.htmlFor);
    const variantId = champ instanceof HTMLElement ? String(champ.dataset.variantId || '') : '';
    if (!/^\d+$/.test(variantId)) return;
    const galerie = cible.closest('.shopify-section')?.querySelector('media-gallery.mpm-galerie');
    const modele = galerie?.querySelector(`template[data-mpm-vedette*=",${variantId},"]`);
    if (galerie && modele instanceof HTMLTemplateElement) prechargerModele(galerie, modele);
  },
  { passive: true }
);

// Après le chargement de la page, quand le navigateur est libre : autres couleurs, puis photos 1024
function prechargementsDeFond() {
  if (ECONOMIE_DONNEES) return;
  const galerie = galeriePrincipale();
  if (!galerie) return;
  prechargerAutresCouleurs(galerie);
  prechargerPhotosHD(galerie);
}
if (document.readyState === 'complete') quandLibre(prechargementsDeFond);
else window.addEventListener('load', () => quandLibre(prechargementsDeFond), { once: true });

// Bouton loupe (délégation : la galerie est remplacée à chaque changement de couleur)
document.addEventListener('click', (event) => {
  const bouton = event.target instanceof Element ? event.target.closest('.mpm-loupe') : null;
  if (!(bouton instanceof HTMLElement)) return;
  if (bouton.closest('.quick-add-modal__content, quick-add-dialog')) return;
  const galerie = bouton.closest('media-gallery');
  if (!galerie) return;

  event.preventDefault();
  visionneuse().ouvrir(galerie, bouton, mediaAffiche(galerie, bouton));
});

/* ------------------------------------------------------------------ */
/* 1. Galerie : nouvelle couleur affichée tout de suite               */
/* ------------------------------------------------------------------ */

let compteurAttente = 0;

/**
 * @param {Element} galerie
 * @param {any} event - shopify:product:select
 */
function afficherCouleurImmediate(galerie, event) {
  const source = event.target instanceof Element ? event.target : null;
  let variantId = event.detail?.variantId;
  if (!variantId && source) {
    const option = source.querySelector('input:checked[data-variant-id], option:checked[data-variant-id]');
    variantId = option instanceof HTMLElement ? option.dataset.variantId : '';
  }
  variantId = String(variantId || '');
  if (!/^\d+$/.test(variantId)) return;

  const modele = galerie.querySelector(`template[data-mpm-vedette*=",${variantId},"]`);
  const conteneur = galerie.querySelector('slideshow-container');
  if (!(modele instanceof HTMLTemplateElement) || !(conteneur instanceof HTMLElement)) return;
  if (conteneur.getClientRects().length === 0) return; // diaporama masqué (grille sur ordinateur)

  conteneur.querySelector('.mpm-galerie__attente')?.remove();
  const attente = document.createElement('div');
  attente.className = 'mpm-galerie__attente';
  attente.setAttribute('aria-hidden', 'true');
  attente.append(modele.content.cloneNode(true));
  conteneur.append(attente);

  compteurAttente += 1;
  const jeton = String(compteurAttente);
  if (galerie instanceof HTMLElement) galerie.dataset.mpmAttente = jeton;

  const nettoyer = () => {
    if (!(galerie instanceof HTMLElement) || galerie.dataset.mpmAttente !== jeton) return;
    delete galerie.dataset.mpmAttente;
    attente.remove();
  };

  // Horizon remplace la galerie dans sa propre réaction à la promesse (enregistrée avant la nôtre) :
  // si la galerie est encore là ensuite, la réponse n'en contenait pas, on retire l'image d'attente.
  if (event.promise && typeof event.promise.then === 'function') {
    event.promise.then(() => nettoyer(), () => nettoyer());
  }
  window.setTimeout(nettoyer, 10000);
}

/* ------------------------------------------------------------------ */
/* 3. Récits : images de la couleur choisie                           */
/* ------------------------------------------------------------------ */

/**
 * @param {Element} actuel
 * @param {Element} nouveau
 */
function memeStructure(actuel, nouveau) {
  const signature = (element) =>
    Array.from(element.querySelectorAll('.mpm-recit'))
      .map((bloc) => `${bloc.getAttribute('data-recit')}:${bloc.className}`)
      .join('|');
  return signature(actuel) === signature(nouveau);
}

/**
 * Reprend les récits de la page renvoyée par le serveur pour la nouvelle variante.
 * @param {Document | undefined} html
 */
function majRecits(html) {
  if (!html || typeof html.getElementById !== 'function') return;

  for (const section of document.querySelectorAll('.shopify-section.mpm-section-recits')) {
    const fraiche = html.getElementById(section.id);
    if (!fraiche) continue;

    const actuel = section.querySelector('mpm-recits');
    const nouveau = fraiche.querySelector('mpm-recits');

    if (actuel && nouveau && memeStructure(actuel, nouveau)) {
      for (const bloc of nouveau.querySelectorAll('.mpm-recit')) {
        const image = bloc.querySelector('img');
        const cible = actuel.querySelector(`.mpm-recit[data-recit="${bloc.getAttribute('data-recit')}"] img`);
        if (!image || !cible || cible.getAttribute('src') === image.getAttribute('src')) continue;
        for (const attribut of ['sizes', 'srcset', 'src', 'alt', 'width', 'height']) {
          const valeur = image.getAttribute(attribut);
          if (valeur === null) cible.removeAttribute(attribut);
          else cible.setAttribute(attribut, valeur);
        }
      }
    } else if (section.innerHTML !== fraiche.innerHTML) {
      section.innerHTML = fraiche.innerHTML;
    }
  }
}

document.addEventListener(EVENEMENT_SELECTION, (event) => {
  const source = event.target;
  if (!(source instanceof Element)) return;
  // Uniquement le sélecteur de la fiche (pas les cartes produit ni l'ajout rapide)
  if (source.closest('product-card, quick-add-dialog, dialog, .quick-add-modal__content')) return;

  const galerie = source.closest('.shopify-section')?.querySelector('media-gallery.mpm-galerie');
  if (galerie && !galerie.closest('dialog')) afficherCouleurImmediate(galerie, event);

  const promesse = /** @type {any} */ (event).promise;
  if (promesse && typeof promesse.then === 'function') {
    promesse
      .then((resultat) => {
        majRecits(resultat?.detail?.html);
        if (!ECONOMIE_DONNEES) {
          quandLibre(() => {
            const galerieAJour = galeriePrincipale();
            if (galerieAJour) prechargerPhotosHD(galerieAJour);
          });
        }
      })
      .catch(() => {});
  }
});

class MpmRecits extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observateur = null;

  // Fondu au défilement pour tous ; le glissement de 12 px n'existe que sans « réduire les animations » (CSS).
  connectedCallback() {
    if (this.dataset.apparition !== 'true') return;
    if (!('IntersectionObserver' in window)) return;
    if (/** @type {any} */ (window).Shopify?.designMode) return;

    const blocs = Array.from(this.querySelectorAll('.mpm-recit:not(.is-visible)'));
    if (!blocs.length) return;

    this.#observateur = new IntersectionObserver(
      (entrees) => {
        for (const entree of entrees) {
          if (!entree.isIntersecting) continue;
          entree.target.classList.add('is-visible');
          this.#observateur?.unobserve(entree.target);
        }
      },
      { threshold: 0.12 }
    );
    this.setAttribute('data-apparition-active', '');
    for (const bloc of blocs) this.#observateur.observe(bloc);
  }

  disconnectedCallback() {
    this.#observateur?.disconnect();
    this.#observateur = null;
  }
}

if (!customElements.get('mpm-recits')) customElements.define('mpm-recits', MpmRecits);

/* ------------------------------------------------------------------ */
/* 4. « Découvrez également »                                         */
/* ------------------------------------------------------------------ */

class MpmDecouvrir extends HTMLElement {
  /** @type {AbortController | null} */
  #controleur = null;
  /** @type {ResizeObserver | null} */
  #observateurTaille = null;
  #charge = false;
  #image = 0;

  connectedCallback() {
    this.piste = /** @type {HTMLElement | null} */ (this.querySelector('.mpm-piste'));
    this.controles = this.querySelector('.mpm-piste-controles');
    this.indicateur = /** @type {HTMLElement | null} */ (this.querySelector('.mpm-progression__indicateur'));
    this.precedent = /** @type {HTMLButtonElement | null} */ (this.querySelector('.mpm-piste-bouton[data-direction="-1"]'));
    this.suivant = /** @type {HTMLButtonElement | null} */ (this.querySelector('.mpm-piste-bouton[data-direction="1"]'));
    if (!this.piste) return;

    this.#controleur = new AbortController();
    const { signal } = this.#controleur;
    const piste = this.piste;

    piste.addEventListener(
      'scroll',
      () => {
        if (this.#image) return;
        this.#image = requestAnimationFrame(() => {
          this.#image = 0;
          this.#maj();
        });
      },
      { passive: true, signal }
    );

    this.precedent?.addEventListener('click', () => this.#defiler(-1), { signal });
    this.suivant?.addEventListener('click', () => this.#defiler(1), { signal });

    // Glisser à la souris (le pavé tactile, Maj + molette et le doigt défilent nativement)
    piste.addEventListener('pointerdown', (event) => this.#debutGlisser(event), { signal });
    piste.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    this.#observateurTaille = new ResizeObserver(() => this.#maj());
    this.#observateurTaille.observe(piste);
    this.#maj();

    if (this.dataset.recommandations !== 'true' && this.dataset.url && !this.#charge) {
      this.#charge = true;
      const lancer = () => this.#charger(signal);
      if ('requestIdleCallback' in window) window.requestIdleCallback(lancer, { timeout: 2000 });
      else window.setTimeout(lancer, 600);
    }
  }

  disconnectedCallback() {
    this.#controleur?.abort();
    this.#controleur = null;
    this.#observateurTaille?.disconnect();
    this.#observateurTaille = null;
    if (this.#image) cancelAnimationFrame(this.#image);
    this.#image = 0;
  }

  /** @param {AbortSignal} signal */
  async #charger(signal) {
    try {
      const reponse = await fetch(String(this.dataset.url), { signal });
      if (!reponse.ok) throw new Error(`Recommandations : ${reponse.status}`);
      const doc = new DOMParser().parseFromString(await reponse.text(), 'text/html');
      const frais = doc.querySelector('mpm-decouvrir');
      const elements = frais ? Array.from(frais.querySelectorAll('.mpm-piste > .mpm-piste__item')) : [];
      const actuels = this.piste ? this.piste.children.length : 0;
      const recommandations = frais?.getAttribute('data-source') === 'recommandations' && elements.length > 0;

      if (recommandations || (actuels === 0 && elements.length > 0)) {
        this.#injecterStyles(doc);
        await this.#remplacer(elements, frais?.getAttribute('data-source') || '');
      }
    } catch (erreur) {
      if (/** @type {any} */ (erreur)?.name === 'AbortError') return;
      /* on garde le repli rendu par le serveur */
    }
    this.#finaliser();
  }

  /** @param {Document} doc */
  #injecterStyles(doc) {
    const style = doc.querySelector('style[data-section-stylesheet]');
    const section = this.closest('.shopify-section');
    if (!style || !section) return;
    const existant = section.querySelector('style[data-section-stylesheet]');
    if (existant) existant.textContent = style.textContent;
    else section.prepend(document.importNode(style, true));
  }

  /**
   * @param {Element[]} elements
   * @param {string} source
   */
  async #remplacer(elements, source) {
    if (!this.piste) return;
    const rect = this.getBoundingClientRect();
    const visible = !this.hidden && rect.bottom > 0 && rect.top < window.innerHeight;
    if (visible) {
      this.classList.add('is-remplacement');
      await new Promise((resolve) => window.setTimeout(resolve, 160));
    }
    this.piste.replaceChildren(...elements.map((element) => document.importNode(element, true)));
    this.piste.scrollLeft = 0;
    if (source) this.dataset.source = source;
    this.classList.remove('is-remplacement');
  }

  #finaliser() {
    const vide = !this.piste || this.piste.children.length === 0;
    if (!(/** @type {any} */ (window).Shopify?.designMode)) this.hidden = vide;
    this.#maj();
  }

  #mesures() {
    const piste = /** @type {HTMLElement} */ (this.piste);
    const premier = piste.querySelector('.mpm-piste__item');
    const largeur = premier ? premier.getBoundingClientRect().width : piste.clientWidth;
    const ecart = parseFloat(getComputedStyle(piste).columnGap) || 0;
    return { largeur, ecart, pas: largeur + ecart };
  }

  /** @param {number} direction */
  #defiler(direction) {
    if (!this.piste) return;
    const { largeur, pas } = this.#mesures();
    if (pas <= 0) return;
    // Nombre de cartes entièrement visibles (5 pour 5,3) : on avance d'une « page »
    const cartesEntieres = Math.max(1, Math.floor((this.piste.clientWidth + pas - largeur) / pas + 0.01));
    this.piste.scrollBy({ left: direction * cartesEntieres * pas, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  #aimanter() {
    if (!this.piste) return;
    const { pas } = this.#mesures();
    if (pas <= 0) return;
    const max = this.piste.scrollWidth - this.piste.clientWidth;
    const cible = Math.min(max, Math.max(0, Math.round(this.piste.scrollLeft / pas) * pas));
    this.piste.scrollTo({ left: cible, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  /** @param {PointerEvent} event */
  #debutGlisser(event) {
    const piste = this.piste;
    if (!piste || event.pointerType !== 'mouse' || event.button !== 0) return;
    if (piste.scrollWidth <= piste.clientWidth + 2) return;

    const x0 = event.clientX;
    const s0 = piste.scrollLeft;
    let glisse = false;

    /** @param {PointerEvent} ev */
    const bouger = (ev) => {
      const dx = ev.clientX - x0;
      if (!glisse) {
        if (Math.abs(dx) < 6) return;
        glisse = true;
        piste.classList.add('is-glisse');
        window.getSelection()?.removeAllRanges();
        try {
          piste.setPointerCapture(event.pointerId);
        } catch {
          /* pointeur déjà relâché */
        }
      }
      piste.scrollLeft = s0 - dx;
    };

    const fin = () => {
      window.removeEventListener('pointermove', bouger);
      window.removeEventListener('pointerup', fin);
      window.removeEventListener('pointercancel', fin);
      if (!glisse) return;

      // Le clic qui suit un glisser n'ouvre pas la fiche
      /** @param {MouseEvent} ev */
      const bloquer = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      window.addEventListener('click', bloquer, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener('click', bloquer, { capture: true }), 400);

      piste.classList.remove('is-glisse');
      this.#aimanter();
    };

    window.addEventListener('pointermove', bouger);
    window.addEventListener('pointerup', fin);
    window.addEventListener('pointercancel', fin);
  }

  #maj() {
    const piste = this.piste;
    if (!piste) return;
    const max = piste.scrollWidth - piste.clientWidth;
    const defilable = max > 2;
    this.controles?.toggleAttribute('data-actif', defilable);

    const x = Math.abs(piste.scrollLeft);
    const debut = !defilable || x <= 2;
    const finPiste = !defilable || x >= max - 2;

    const focus = document.activeElement;
    if (this.precedent) this.precedent.disabled = debut;
    if (this.suivant) this.suivant.disabled = finPiste;
    if (focus === this.suivant && finPiste && !debut) this.precedent?.focus({ preventScroll: true });
    if (focus === this.precedent && debut && !finPiste) this.suivant?.focus({ preventScroll: true });

    if (this.indicateur && defilable) {
      const taille = Math.min(1, Math.max(0.08, piste.clientWidth / piste.scrollWidth));
      const position = Math.min(1, Math.max(0, x / max));
      this.indicateur.style.setProperty('--mpm-taille', taille.toFixed(4));
      this.indicateur.style.setProperty('--mpm-position', position.toFixed(4));
    }
  }
}

if (!customElements.get('mpm-decouvrir')) customElements.define('mpm-decouvrir', MpmDecouvrir);
